import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadModulesForRoom } from "../lib/content";
import { courseLessons } from "../lib/curriculum";
import { ROOM_CONFIG, SUBJECT_THEME } from "../config";
import type { Module } from "../types";
import { Hero } from "../components/Hero";
import { Icon } from "../components/Icon";
import { IconoIlustrado } from "../components/IconoIlustrado";
import { iconoDeMateria } from "../lib/iconosIlustrados";
import { Cargando } from "../components/Cargando";
import { useParticipantGrade } from "../lib/access";
import { gradoDeContenido } from "../lib/salas";

function lessonsForGrade(m: Module, grade: string): number {
  const lessons = courseLessons(m.lessons);
  if (grade === "Todos") return lessons.length;
  return lessons.filter((l) => l.grade === grade).length;
}

export function RoomPage({ room, sinHero = false }: { room: string; sinHero?: boolean }) {
  // `sinHero`: la página de IA embebe esta lista dentro de su propio scroll y ya
  // tiene su hero arriba. Dos heroes seguidos leen como dos páginas pegadas.
  const cfg = ROOM_CONFIG[room];
  const [modules, setModules] = useState<Module[] | null>(null);
  const [error, setError] = useState("");
  const [gradeElegido, setGradeElegido] = useState("Todos");
  const participantGrade = useParticipantGrade();
  const navigate = useNavigate();
  const modulePath = (slug: string) => {
    const path = `/${room}/${slug}`;
    return grade === "Todos" ? path : `${path}?grado=${encodeURIComponent(grade)}`;
  };

  useEffect(() => {
    setModules(null);
    setGradeElegido("Todos");
    loadModulesForRoom(room)
      .then(setModules)
      .catch((e) => setError(e.message));
  }, [room]);

  // Filtros de grado derivados del contenido de la sala.
  const grades = useMemo(() => {
    if (!modules) return ["Todos"];
    const set = new Set<string>();
    // Los electivos crossGrade (STEM, "Nivel 1-3") no aportan grados escolares
    // al filtro: mezclarían "Nivel 1" con 7mo/8vo/9no.
    modules.filter((m) => !m.crossGrade).forEach((m) => courseLessons(m.lessons).forEach((l) => set.add(l.grade)));
    return ["Todos", ...[...set].sort((a, b) => a.localeCompare(b, "es", { numeric: true }))];
  }, [modules]);

  /*
   * Un participante ve SOLO su grado, y sin selector: enseñarle 1ro–6to cuando
   * cursa primero confunde más de lo que aporta (decisión de Alida, 2026-08-19,
   * viendo la primera cuenta real).
   *
   * Se fija solo si su grado existe en el contenido de esta sala. Si no —una sala
   * cuyo contenido no cubre su grado—, se le deja el selector: es preferible que
   * pueda moverse a que se quede mirando una rejilla vacía sin entender por qué.
   *
   * El staff no tiene grado (contexto null), así que conserva el selector completo.
   */
  // Kínder no tiene contenido propio: ve el de 1ro (ver gradoDeContenido).
  const gradoContenido = gradoDeContenido(participantGrade);
  const gradoFijo = gradoContenido && grades.includes(gradoContenido) ? gradoContenido : null;
  const grade = gradoFijo ?? gradeElegido;

  const visible = useMemo(() => {
    if (!modules) return [];
    // Los electivos crossGrade se muestran con cualquier grado seleccionado.
    return modules.filter((m) => m.crossGrade || lessonsForGrade(m, grade) > 0);
  }, [modules, grade]);

  // Lo que el hero enseña como datos de la sala: exactamente lo que hay debajo,
  // contado sobre el mismo filtro. Antes esto era `PROGRAMA.lessons`, un 79 fijo
  // en config.ts que se repetía igual en las doce pantallas — solo Elemental pasa
  // de mil lecciones.
  const totalLecciones = useMemo(
    () =>
      visible.reduce(
        (n, m) => n + (m.crossGrade ? courseLessons(m.lessons).length : lessonsForGrade(m, grade)),
        0
      ),
    [visible, grade]
  );

  if (!cfg) return <div className="center-msg">Sala desconocida.</div>;
  if (error) return <div className="center-msg">No se pudo cargar el contenido: {error}</div>;
  if (!modules) return <Cargando texto="Cargando la sala…" />;

  /*
   * La clase de sala (`sala-ninos` / `sala-superior`) ya NO se pone aquí: la
   * aplica App.tsx sobre toda la rama de la ruta, para que valga también en la
   * materia y en la lección. Ver `roomClassFor` en config.ts.
   */
  return (
    <div>
      {!sinHero && (
        <Hero
          room={cfg.heroRoom}
          kicker={cfg.kicker}
          title={cfg.title}
          subtitle={cfg.subtitle}
        />
      )}

      {/*
        Las cifras bajaron del hero a aquí.
        Dentro del hero eran dos cajas translúcidas de 60 px de alto sobre la
        foto: se leían como adorno y costaban un tercio de lo que ahora mide el
        hero entero. Sobre la cabecera de la rejilla que cuentan, se leen como lo
        que son — cuántas materias y cuántas lecciones hay AHÍ DEBAJO, con el
        filtro de grado que esté puesto.
      */}
      <div className="section-head">
        <h2>{cfg.sectionTitle}</h2>
        <span className="muted">
          {visible.length} {visible.length === 1 ? "materia" : "materias"}
          {" · "}
          {totalLecciones.toLocaleString("es")} {totalLecciones === 1 ? "lección" : "lecciones"}
          {/* "Alineado a estándares DEPR" se queda: es la promesa curricular
              que la AVP enseña, y sale gratis en la misma línea. */}
          {" · "}{cfg.sectionMuted}
        </span>
      </div>

      {gradoFijo ? (
        // Sin selector, pero se dice en qué grado está: si no, una rejilla más
        // corta de lo esperado parece contenido que falta.
        <div className="grade-filters">
          <button className="active" disabled>{gradoFijo}</button>
        </div>
      ) : grades.length > 1 && (
        <div className="grade-filters">
          {grades.map((g) => (
            <button key={g} className={g === grade ? "active" : ""} onClick={() => setGradeElegido(g)}>
              {g}
            </button>
          ))}
        </div>
      )}

      <div className="card-grid">
        {visible.map((m) => {
          // Electivo crossGrade: el conteo no depende del grado seleccionado.
          const count = m.crossGrade ? courseLessons(m.lessons).length : lessonsForGrade(m, grade);
          const theme = SUBJECT_THEME[m.slug];

          // Materias con mascota (salas de niños): tarjeta ilustrada a color.
          if (theme) {
            return (
              <button
                key={m.slug}
                className="card card-mascota"
                style={
                  { "--acc": theme.accent, "--acc2": theme.accent2 } as React.CSSProperties
                }
                onClick={() => navigate(modulePath(m.slug))}
              >
                <div className="mascota-banda">
                  {/*
                    Elemental: la mascota manda, y el objeto de la materia la
                    acompaña apoyado en el mismo suelo.

                    Intermedia y Superior no tienen animal (decisión vieja: a un
                    adolescente las mascotas le infantilizan), así que ahí el
                    objeto ES el protagonista y se pinta en grande a la
                    izquierda. Antes esas dos salas usaban `iconos-materias/`,
                    un pack PNG de OTRO estilo plano: dos idiomas gráficos para
                    la misma rejilla. Ahora es el mismo set en las tres.

                    Ruta ABSOLUTA en la mascota: con `base: "/"` una relativa se
                    resolvería contra la ruta actual y fallaría fuera de la raíz.
                  */}
                  {theme.animal && (
                    <img
                      className="mascota"
                      src={`/assets/animales/${theme.animal}.svg`}
                      alt=""
                      aria-hidden="true"
                      /* Si el SVG no está, se oculta y la tarjeta se queda con su
                         color. Una sala no se rompe por un dibujo que falta. */
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  )}
                  <IconoIlustrado
                    nombre={iconoDeMateria(m.slug)}
                    size={theme.animal ? 68 : 64}
                    className={theme.animal ? "mascota-prop" : "mascota-prop mascota-prop-sola"}
                  />
                </div>
                <div className="card-cuerpo">
                  <h3>{m.title}</h3>
                  <p>{m.overview}</p>
                  {/* La insignia de lecciones baja de la banda al pie de la
                      tarjeta. Arriba competía con la mascota por ser lo primero
                      que se mira, y con los animales apaisados —el gato de
                      Estudios Sociales mide 151 px de ancho— directamente se
                      solapaban. La banda queda para el animal y nada más. */}
                  <div className="card-pie">
                    <span className="tag">{m.gradeBand.toUpperCase()}</span>
                    <span className="count-badge">
                      {count} {count === 1 ? "lección" : "lecciones"}
                    </span>
                  </div>
                </div>
              </button>
            );
          }

          return (
            <button key={m.slug} className="card" onClick={() => navigate(modulePath(m.slug))}>
              <div className="card-top">
                <Icon name={m.icon} size={30} className={`icon-${cfg.accent}`} />
                <span className="count-badge">
                  {count} {count === 1 ? "lección" : "lecciones"}
                </span>
              </div>
              <h3>{m.title}</h3>
              <p>{m.overview}</p>
              <span className="tag">{m.gradeBand.toUpperCase()}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
